/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type NextApiRequest } from 'next';
import {
  type ContentStackPremiumPdp,
  type FormattedContentStackPremiumPDP,
} from '../../../interface';
import { getContentByContentType } from '../../common/getContentByContentType';
import { type PageContextType } from '@/next-types';
import { type ParsedUrlQuery } from 'querystring';
import { getLocaleForProxiedHostname, getProxiedHostname } from '@/utils/getInitialAppState';
import { LANDING_PAGE_FIELDS } from '../../landingPage/getContentStackLandingPageContent';
import { cache } from '@/utils/serverQueryCache';
import { deepFilterAndSegmentContent } from '../../../utils/deepFilterAndSegmentContent';
import { getPremiumPDPCacheKey } from '../../../utils/cache/getPremiumPDPCacheKey';

const CACHE_TIME = 1000 * 60 * 15;

export const getContentStackPremiumPDPContent = async (
  req: NextApiRequest | PageContextType['req'],
  query: ParsedUrlQuery,
  userSegments: string[] = [],
): Promise<FormattedContentStackPremiumPDP> => {
  const proxiedHostname = getProxiedHostname(req);
  const language = getLocaleForProxiedHostname(proxiedHostname).toLowerCase();
  let response = cache.getItem<ContentStackPremiumPdp>(getPremiumPDPCacheKey(language));

  if (!response) {
    response = await getContentByContentType<ContentStackPremiumPdp>({
      contentTypeUid: 'premium_pdp',
      referenceFieldPath: [
        'pdp_page_body.banner.banner',
        'pdp_page_body.pdp_slot1.pdp_slot1',
        'pdp_page_body.pdp_slot2.pdp_slot2',
        ...LANDING_PAGE_FIELDS.map(
          (field) => `pdp_page_body.pdp_slot1.pdp_slot1.pdp_slot_content.${field}`,
        ),
        ...LANDING_PAGE_FIELDS.map(
          (field) => `pdp_page_body.pdp_slot2.pdp_slot2.pdp_slot_content.${field}`,
        ),
        'pdp_page_body.certona_middle_content.certona',
        'pdp_page_body.certona_bottom_content.certona',
        'pdp_page_body.certona_content_2.certona',
        'pdp_page_body.bazaar_voice_questions.bazaar_voice_questions',
        'pdp_page_body.target_div.target_div',
        'pdp_page_body.buy_again_container.buy_again_container',
        'pdp_page_body.pencil_banner.pencil_banner',
        'prefooter.prefooter',
      ],
      req,
      query,
    });

    cache.setItem(getPremiumPDPCacheKey(language), response, CACHE_TIME);
  }

  const taxonomyPath = Array.isArray(query.taxonomyPath)
    ? `/${query.taxonomyPath.join('/')}`
    : query.taxonomyPath;
  const brandName = query.brandName?.toString() ?? '';
  const { pdp_page_body, prefooter } = response;

  let pageBody = pdp_page_body;

  if (taxonomyPath) {
    pageBody = deepFilterAndSegmentContent({
      item: pageBody,
      taxonomyPath,
      userSegments,
      brandName,
    });
  }

  return {
    pageBody,
    prefooter,
  };
};
